/**
 * @module pages/CheckoutPage
 * @description Brief §4.8 checkout: donation summary, then donor fields, then
 * hand-off to Neon's secure payment. Tier 1 = one page. Tier 2 swaps the
 * left column for the guided one-question-per-screen flow (FEATURES flag).
 */
import { useState, type JSX } from "react";
import { useGiving } from "../app/GivingContext";
import { navigate } from "../app/router";
import { CartLineRow } from "../components/Cart";
import { DonorFields } from "../components/DonorFields";
import { GuidedCheckout } from "../components/GuidedCheckout";
import { Icon } from "../components/Icons";
import { TrustList } from "../components/Trust";
import { FEATURES } from "../config/tier";
import { isValid, validateDonor, type DonorErrors } from "../domain/donor";
import type { CartLine } from "../domain/types";
import { formatUsd, plural } from "../lib/format";
import "./Checkout.css";

/** Order summary column, shared by both checkout styles. */
export function OrderSummary({ editable }: { editable: boolean }): JSX.Element {
    const { lines, totalUsd } = useGiving();
    return (
        <aside className="checkout__summary" aria-labelledby="summary-title">
            <h2 id="summary-title" className="checkout__h2">Donation summary</h2>
            <ul className="checkout__lines">
                {lines.map((line: CartLine) => <CartLineRow key={line.causeId} line={line} editable={editable} />)}
            </ul>
            <div className="checkout__total"><span>{plural(lines.length, "cause")}</span><strong>{formatUsd(totalUsd)}</strong></div>
            <p className="checkout__fine">One payment, designated to each cause above. Tax-deductible gift to The American Friends of Jamaica, a 501(c)(3).</p>
            <TrustList compact />
        </aside>
    );
}

/** Checkout page. */
export function CheckoutPage(): JSX.Element {
    const { lines, donor, setDonor, paymentFailed } = useGiving();
    const [errors, setErrors] = useState<DonorErrors>({});

    if (lines.length === 0) {
        return (
            <main className="container checkout checkout--empty">
                <h1>Your cart is empty</h1>
                <p>Add a cause or two first, then give to all of them in one checkout.</p>
                <button type="button" className="btn btn--primary btn--lg" onClick={() => navigate("browse")}>Browse causes</button>
            </main>
        );
    }

    /** Validate, then hand off to Neon (simulated). */
    const proceed = (): void => {
        const found: DonorErrors = validateDonor(donor);
        setErrors(found);
        if (isValid(found)) navigate("payment");
    };

    return (
        <main className="container checkout">
            <button type="button" className="checkout__back" onClick={() => navigate("browse")}><Icon name="chevron-left" size={16} /> Continue browsing</button>
            <h1 className="checkout__h1">Secure checkout</h1>

            {paymentFailed && (
                <div className="checkout__alert" role="alert">
                    <strong>Your payment didn't go through (simulated).</strong> Nothing was charged. Check your details and try again.
                </div>
            )}

            <div className="checkout__grid">
                <section className="checkout__form" aria-labelledby="donor-title">
                    {FEATURES.guidedCheckout ? (
                        <GuidedCheckout onComplete={proceed} errors={errors} setErrors={setErrors} />
                    ) : (
                        <>
                            <h2 id="donor-title" className="checkout__h2">Your details</h2>
                            <DonorFields donor={donor} errors={errors} onChange={setDonor} />
                            <div className="checkout__pay-note"><Icon name="lock" /> Next, you'll pay on Neon's secure payment page. Card details never touch this site.</div>
                            <button type="button" className="btn btn--primary btn--lg btn--block" onClick={proceed}>
                                Continue to secure payment <Icon name="arrow-right" />
                            </button>
                        </>
                    )}
                </section>
                <OrderSummary editable={!FEATURES.guidedCheckout} />
            </div>
        </main>
    );
}
